import React, { useState } from 'react';
import { Activity, CheckCircle, XCircle, Loader2, Plus, Minus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const AdaptestTester = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [itemCount, setItemCount] = useState(4);
  const [responses, setResponses] = useState([2, 3, 1, 2]);
  const [customItems, setCustomItems] = useState('');
  const [useCustomItems, setUseCustomItems] = useState(false);

  const sampleItems = [
    { thresholds: [-2.5, -0.5, 1.5], slope: 1.2, label: 'Easy item, good discrimination' },
    { thresholds: [-1.5, 0.5, 2.0], slope: 1.5, label: 'Medium difficulty' },
    { thresholds: [-0.5, 1.0, 2.5], slope: 1.0, label: 'Harder item' },
    { thresholds: [0.0, 1.5, 3.0], slope: 1.8, label: 'Hardest item, high discrimination' },
  ];

  const handleResponseChange = (index, value) => {
    const newResponses = [...responses];
    newResponses[index] = parseInt(value) || 0;
    setResponses(newResponses);
  };

  const addItem = () => {
    setItemCount(prev => prev + 1);
    setResponses(prev => [...prev, 0]);
  };

  const removeItem = () => {
    if (itemCount > 1) {
      setItemCount(prev => prev - 1);
      setResponses(prev => prev.slice(0, -1));
    }
  };

  const runTest = async () => {
    setLoading(true);
    setResult(null);

    try {
      let items;
      if (useCustomItems && customItems) {
        try {
          items = JSON.parse(customItems);
        } catch (e) {
          toast.error('Invalid JSON for custom items');
          setLoading(false);
          return;
        }
      } else {
        items = sampleItems.slice(0, itemCount).map(item => [
          item.thresholds,
          [item.slope],
          [0]
        ]);
      }

      const response = await api.post('/api/dev/test-adaptest/', {
        items: items,
        responses: responses.slice(0, itemCount)
      });

      setResult(response.data);
      if (response.data.success) {
        toast.success('Adaptest API call successful!');
      } else {
        toast.error('Adaptest API call failed');
      }
    } catch (error) {
      console.error('Adaptest test error:', error);
      setResult({
        success: false,
        error: error.response?.data?.error || error.message
      });
      toast.error('Failed to call Adaptest API');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Adaptest IRT Scoring API Tester</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Test the external Adaptest service for IRT-based score calculations
          </p>
        </div>
        <div className="code-chip">
          https://adaptest.azurewebsites.net/
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">IRT Item Parameters</h4>
          <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={useCustomItems}
              onChange={(e) => setUseCustomItems(e.target.checked)}
              className="mr-2 h-4 w-4"
            />
            Use custom JSON
          </label>
        </div>

        {useCustomItems ? (
          <textarea
            value={customItems}
            onChange={(e) => setCustomItems(e.target.value)}
            placeholder='[[[-2.5, -0.5, 1.5], [1.2], [0]], ...]'
            className="input h-32 font-mono"
          />
        ) : (
          <div className="space-y-2">
            {sampleItems.slice(0, itemCount).map((item, idx) => (
              <div key={idx} className="flex items-center rounded-lg border border-gray-200 bg-white p-2 text-sm dark:border-gray-700 dark:bg-gray-800">
                <span className="w-16 font-medium text-gray-700 dark:text-gray-200">Item {idx + 1}:</span>
                <span className="flex-1 font-mono text-xs text-gray-500 dark:text-gray-400">
                  thresholds={JSON.stringify(item.thresholds)}, slope={item.slope}
                </span>
                <span className="text-xs italic text-gray-400">{item.label}</span>
              </div>
            ))}
            <div className="mt-2 flex gap-2">
              <button
                onClick={removeItem}
                disabled={itemCount <= 1}
                className="btn btn-sm btn-danger-soft border border-danger-500/25"
              >
                <Minus className="h-3.5 w-3.5" />
                Remove
              </button>
              <button
                onClick={addItem}
                disabled={itemCount >= sampleItems.length}
                className="btn btn-sm btn-secondary"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Item
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
        <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Participant Responses (0-indexed)</h4>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {responses.slice(0, itemCount).map((resp, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Item {idx + 1}:</label>
              <input
                type="number"
                min="0"
                max="3"
                value={resp}
                onChange={(e) => handleResponseChange(idx, e.target.value)}
                className="input h-8 w-16 px-2 text-center"
              />
            </div>
          ))}
        </div>
        <p className="hint">
          For 4-category items: 0=lowest, 1=second, 2=third, 3=highest
        </p>
      </div>

      <div className="flex justify-end">
        <button
          onClick={runTest}
          disabled={loading}
          className="btn btn-primary btn-lg"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Calling Adaptest...
            </>
          ) : (
            <>
              <Activity className="h-4 w-4" />
              Run IRT Scoring Test
            </>
          )}
        </button>
      </div>

      {result && (
        <div className={`rounded-lg border p-4 ${result.success ? 'border-success-500/25 bg-success-50 dark:bg-success-500/10' : 'border-danger-500/25 bg-danger-50 dark:bg-danger-500/10'}`}>
          <div className="mb-3 flex items-center">
            {result.success ? (
              <CheckCircle className="mr-2 h-4 w-4 text-success-600 dark:text-success-400" />
            ) : (
              <XCircle className="mr-2 h-4 w-4 text-danger-600 dark:text-danger-400" />
            )}
            <span className={`text-sm font-semibold ${result.success ? 'text-success-800 dark:text-success-400' : 'text-danger-800 dark:text-danger-400'}`}>
              {result.success ? 'API Call Successful' : 'API Call Failed'}
            </span>
          </div>

          {result.success ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-white p-4 text-center dark:border-gray-700 dark:bg-gray-800">
                  <div className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">{result.eap?.toFixed(4)}</div>
                  <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">EAP (Theta)</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 text-center dark:border-gray-700 dark:bg-gray-800">
                  <div className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">{result.sem?.toFixed(4)}</div>
                  <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">SEM</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 text-center dark:border-gray-700 dark:bg-gray-800">
                  <div className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">{result.t_score?.toFixed(2)}</div>
                  <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">T-Score (EAP×10+50)</div>
                </div>
              </div>

              {result.processing_log && result.processing_log.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                  <h5 className="mb-2 text-sm font-medium text-gray-800 dark:text-gray-200">Processing Log</h5>
                  <div className="space-y-1 font-mono text-xs text-gray-600 dark:text-gray-400">
                    {result.processing_log.map((log, idx) => (
                      <div key={idx}>{log}</div>
                    ))}
                  </div>
                </div>
              )}

              <details className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                <summary className="cursor-pointer text-sm font-medium text-gray-800 dark:text-gray-200">Raw API Response</summary>
                <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-gray-50 p-2 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </div>
          ) : (
            <div className="text-sm text-danger-700 dark:text-danger-400">
              <p className="font-medium">Error:</p>
              <p>{result.error}</p>
            </div>
          )}
        </div>
      )}

      <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
        <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">About IRT Scoring</h4>
        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 [&_strong]:font-semibold [&_strong]:text-gray-700 dark:[&_strong]:text-gray-300">
          <li><strong>EAP (Expected A Posteriori):</strong> The estimated trait level (theta) on a standard normal scale (-3 to +3)</li>
          <li><strong>SEM (Standard Error of Measurement):</strong> Precision of the estimate (lower is better)</li>
          <li><strong>T-Score:</strong> Rescaled score using formula (EAP × 10) + 50, giving a mean of 50 and SD of 10</li>
          <li><strong>Thresholds:</strong> Difficulty parameters for each response category transition</li>
          <li><strong>Slope:</strong> Discrimination parameter (higher = item better differentiates trait levels)</li>
        </ul>
      </div>
    </div>
  );
};

export default AdaptestTester;
